import { React, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import "./point-store-style.css";
import Loading from "../../dialog/loading/loading";
import GoogleLoginDialog from "../../dialog/google-login/GoogleLoginDialog";
import ConfirmDialog from "../../dialog/confirm/confirm-dialog";
import AuthService from "../../../service/auth-service";
import UserService from "../../../service/user-service";
import PaymentService from "../../../service/payment-service";
import SiteFooter from "../../custom/site-footer/site-footer";
import useIsMobile from "../../../hooks/useIsMobile";
import { trackPurchase } from "../../../utils/meta-pixel";

//planID → 顯示名（儲值 Purchase 事件用）
const POINT_PLAN_LABEL = {
  point_300: "A-Point 儲值卡 300 點",
  point_1000: "A-Point 儲值卡 1000 點",
  point_2100: "A-Point 儲值卡 2100 點",
  point_3300: "A-Point 儲值卡 3300 點",
};

const PointStoreCompontent = ({ setShowServiceMenu, setShowPersonalMenu }) => {
  let navigate = useNavigate();
  let location = useLocation();
  const registerFrom = location.pathname;

  const isMobile = useIsMobile();
  const [point, setPoint] = useState(0);
  const [isLoading, setIsLoading] = useState(true);

  //—— Auth gating ——
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [showRegisterPrompt, setShowRegisterPrompt] = useState(false);

  // 抓取用戶最新點數（純資料、不做 auth 決策）
  const fetchPoint = async () => {
    try {
      const response = await UserService.getPersonalInfo();
      if (response.data.status && response.data.data?.userValidCode === "2") {
        setPoint(response.data.data.userAPoint || 0);
      }
    } catch (e) {
      console.error("獲取資料發生錯誤：", e);
    }
  };

  // 初始載入：先 auth gating，通過才拉點數
  useEffect(() => {
    (async () => {
      const start = Date.now();
      const isLogin = AuthService.getLoginStatus();
      const profile = AuthService.getUserProfile();
      if (!isLogin || !profile) {
        setShowLoginDialog(true);
      } else if (profile.userValidCode !== "2") {
        setShowRegisterPrompt(true);
      } else {
        await fetchPoint();
      }
      const remaining = 1000 - (Date.now() - start);
      if (remaining > 0) {
        await new Promise((r) => setTimeout(r, remaining));
      }
      setIsLoading(false);
    })();
  }, []);

  // 用戶從其他分頁切回此頁時，自動刷新點數（付款完成後無感更新）
  //  只有 validCode=2 才會 refetch；其他狀態維持 dialog 提示
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible") return;
      const profile = AuthService.getUserProfile();
      if (AuthService.getLoginStatus() && profile?.userValidCode === "2") {
        fetchPoint();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  //Meta Pixel Purchase 偵測（儲值）
  //  /checkout 導回時 location.state.pendingOrderID 會帶訂單號
  //  打後端 checkOrderPaid → paid=true 就 fire Purchase + 清 state
  useEffect(() => {
    const pendingOrderID = location.state?.pendingOrderID;
    if (!pendingOrderID) return;

    let cancelled = false;
    const checkAndFire = async () => {
      if (document.hidden || cancelled) return;
      try {
        const res = await PaymentService.checkOrderPaid(pendingOrderID);
        if (cancelled) return;
        const d = res.data?.data;
        if (d?.paid) {
          const planID = d.planID || "";
          trackPurchase({
            productID: planID || "point_topup",
            productName: POINT_PLAN_LABEL[planID] || "A-Point 儲值",
            amount: d.amount || 0,
          });
          //清 location.state 避免重複觸發
          navigate(location.pathname, { replace: true, state: {} });
        }
      } catch (e) {
        //靜默失敗（追蹤事件不擋主流程）
      }
    };

    checkAndFire(); //掛載立即跑一次
    document.addEventListener("visibilitychange", checkAndFire);
    return () => {
      cancelled = true;
      document.removeEventListener("visibilitychange", checkAndFire);
    };
  }, [location.state, location.pathname, navigate]);

  const handleLoginSuccess = async ({ idToken } = {}) => {
    if (!idToken) return;
    try {
      const res = await AuthService.firebaseLogin(idToken);
      if (res.data.status) {
        AuthService.login(res.data.data);
        setShowLoginDialog(false);
        if (res.data.data.userValidCode !== "2") {
          navigate("/register", { state: { from: registerFrom } });
        } else {
          fetchPoint();
        }
      }
    } catch (e) {
      console.error("[PointStore] firebase-login 失敗:", e);
    }
  };

  const pointCards = [
    { planID: "point_300", point: 300, money: 99 },
    { planID: "point_1000", point: 1000, money: 299 },
    { planID: "point_2100", point: 2100, money: 599 },
    { planID: "point_3300", point: 3300, money: 899 },
  ];

  //點方案卡 → 導到 /checkout（結帳頁選發票、送出、跳綠界）
  //  付款完成後綠界 callback 會加點數；用戶切回 /point 時 visibilitychange 會 refetch
  const handleBuy = (planID) => {
    const info = pointCards.find((p) => p.planID === planID);
    if (!info) return;
    navigate("/checkout", {
      state: {
        planID: info.planID,
        itemName: `A-Point 儲值卡 ${info.point} 點`,
        amount: info.money,
        returnPath: "/point",
      },
    });
  };

  return (
    <div className="point-store-page">
      {/* API 載入中（初始至少 1 秒）*/}
      {isLoading && <Loading />}

      {/* 電腦板佈局 */}
      {!isMobile && (
        <div
          className="web-main"
          onMouseEnter={() => {
            setShowServiceMenu(false);
            setShowPersonalMenu(false);
          }}
        >
          <div className="title">
            <img src="../images/ic-apoint.png"></img>
            <span>點數管理</span>
          </div>

          <div className="point-store-card">
            <span
              className="record-link"
              onClick={() => navigate("/point-record")}
            >
              點數紀錄
            </span>
            <img
              className="display-flag"
              src="../images/ic-yellow-apoint.png"
            ></img>

            <div className="points-content">
              <span>目前擁有</span>
              <span className="points">{point ? point : 0}</span>
              <span>POINTS</span>
            </div>

            <div className="line"></div>

            <span className="point-title">儲值點數</span>

            <div className="point-cards">
              {pointCards.map((info) => {
                return (
                  <div
                    className="item-card"
                    onClick={() => handleBuy(info.planID)}
                    style={{ cursor: "pointer" }}
                  >
                    <div className="card-info">
                      <div className="name">
                        <span>A-POINT</span>
                        <span className="store">儲值卡</span>
                      </div>

                      <div className="price">
                        <img src="../images/ic-white-apoint.png"></img>
                        <span>{info.point}</span>
                      </div>
                    </div>

                    <span className="card-point">{`儲值 ${info.point} points`}</span>

                    <span className="card-money">{`NT$${info.money}`}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="divide"></div>

          {/*隱私政策與聯絡官方*/}
          <SiteFooter />
        </div>
      )}

      {isMobile && (
        <div className="mobile-main">
          <div className="m-content">
            <div className="m-title">
              <img src="../images/ic-apoint.png" />
              <span>點數管理</span>
            </div>

            <div className="m-point-store-card">
              <span
                className="m-record-link"
                onClick={() => navigate("/point-record")}
              >
                點數紀錄
              </span>
              <img
                className="m-display-flag"
                src="../images/ic-yellow-apoint.png"
              />

              <div className="m-points-content">
                <span>目前擁有</span>
                <span className="m-points">{point ? point : 0}</span>
                <span>POINTS</span>
              </div>

              <div className="m-line"></div>

              <div className="m-point-cards">
                {pointCards.map((info) => (
                  <div
                    className="m-item-card"
                    key={info.planID}
                    onClick={() => handleBuy(info.planID)}
                  >
                    <div className="m-card-info">
                      <div className="m-name">
                        <span>A-POINT</span>
                        <span className="m-store">儲值卡</span>
                      </div>
                      <div className="m-price">
                        <img src="../images/ic-white-apoint.png" />
                        <span>{info.point}</span>
                      </div>
                    </div>
                    <span className="m-card-point">{`儲值 ${info.point} points`}</span>
                    <span className="m-card-money">{`NT$${info.money.toLocaleString()}`}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          {/* m-content */}

          <SiteFooter />
        </div>
      )}

      {/* 未登入訪客；關掉 → 留在當前頁 */}
      {showLoginDialog && (
        <GoogleLoginDialog
          open={showLoginDialog}
          onClose={() => {
            setShowLoginDialog(false);
          }}
          onSuccess={handleLoginSuccess}
        />
      )}

      {/* 已登入但未完成註冊；關掉 → 留在當前頁 */}
      <ConfirmDialog
        open={showRegisterPrompt}
        onClose={() => {
          setShowRegisterPrompt(false);
        }}
        onSuccess={() => {
          setShowRegisterPrompt(false);
          navigate("/register", { state: { from: registerFrom } });
        }}
        title="尚未完成註冊"
        message="點數管理需要完成註冊才能查看，<br>是否前往註冊頁完成？"
        confirmTxt="前往註冊"
      />
    </div>
  );
};

export default PointStoreCompontent;
